import { useEffect, useMemo } from "react";
import { createSelector } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";

import {
  ordersApi,
  useGetMasterOrderQuery,
  useGetOrderDetailQuery,
} from "./ordersApi";
import { aggregateMaster } from "./masterAggregate";

const EMPTY = [];

const useSubOrderDetails = (ids) => {
  const dispatch = useDispatch();
  const key = ids.join(",");

  useEffect(() => {
    if (!key) return undefined;

    const subscriptions = key
      .split(",")
      .map((id) =>
        dispatch(ordersApi.endpoints.getOrderDetail.initiate(Number(id)))
      );

    return () => subscriptions.forEach((subscription) => subscription.unsubscribe());
  }, [dispatch, key]);

  const selectAll = useMemo(() => {
    if (!key) return () => EMPTY;

    const selectors = key
      .split(",")
      .map((id) => ordersApi.endpoints.getOrderDetail.select(Number(id)));

    return createSelector(selectors, (...results) => results);
  }, [key]);

  return useSelector(selectAll);
};

export const useOrderDetail = (orderId) => {
  const detailQuery = useGetOrderDetailQuery(orderId, { skip: !orderId });

  const detail = detailQuery.data;
  const masterRef = detail?.master;
  const isMaster = Boolean(masterRef && masterRef.subOrderCount > 1);

  const masterQuery = useGetMasterOrderQuery(masterRef?.id, {
    skip: !isMaster,
  });

  const master = masterQuery.data;

  const subOrderIds = useMemo(() => {
    if (!master?.subOrders?.length) return EMPTY;

    return [...master.subOrders]
      .sort(
        (a, b) =>
          (a.position ?? Number.MAX_SAFE_INTEGER) -
          (b.position ?? Number.MAX_SAFE_INTEGER)
      )
      .map((sub) => sub.id);
  }, [master]);

  const subResults = useSubOrderDetails(subOrderIds);

  const sections = useMemo(() => {
    if (!master) return EMPTY;

    const byId = new Map(master.subOrders.map((sub) => [sub.id, sub]));

    return subOrderIds.map((id, index) => {
      const result = subResults[index];
      const info = byId.get(id);

      return {
        id,
        orderNumber: info?.orderNumber || String(id),
        supplier: info?.supplier,
        detail: result?.data || null,
        loading: !result?.data && !result?.isError,
        errored: Boolean(result?.isError),
      };
    });
  }, [master, subOrderIds, subResults]);

  const loadedDetails = useMemo(
    () => sections.map((section) => section.detail).filter(Boolean),
    [sections]
  );

  const aggregate = useMemo(
    () => (isMaster ? aggregateMaster(loadedDetails) : null),
    [isMaster, loadedDetails]
  );

  const cancellableIds = useMemo(() => {
    if (isMaster) return (aggregate?.cancellable || []).map((sub) => sub.id);

    return detail?.cancellable ? [detail.id] : EMPTY;
  }, [isMaster, aggregate, detail]);

  const refetch = useMemo(
    () => () => {
      detailQuery.refetch();
      if (isMaster) masterQuery.refetch();
    },
    [detailQuery, masterQuery, isMaster]
  );

  return {
    detail,
    isMaster,
    master,
    masterRef,
    sections,
    aggregate,
    cancellableIds,
    status: isMaster ? aggregate?.status || detail?.status : detail?.status,
    orderNumber: isMaster ? master?.orderNumber || masterRef?.orderNumber : detail?.orderNumber,
    isLoading: detailQuery.isLoading || (isMaster && masterQuery.isLoading),
    errorKind: detailQuery.isError ? detailQuery.error?.kind || "network" : null,
    refetch,
  };
};
